import React, { useEffect, useState } from 'react';
import { getProvisioningUri, confirmOtp } from '../../../api/users';
import { identifyFullStoryUser } from '../../../utils/fullstory';
import { handleUserRedirect } from '../../../utils/userRedirect';
import { useHistory } from 'react-router-dom';

import OtpEntry from '../otp_entry';
import QRCode from 'react-qr-code';

import styles from './index.module.css';

const OtpRegistration = ({ onComplete }) => {
  const history = useHistory();
  const [provisioningUri, setProvisioningUri] = useState(null);
  const [secretKey, setSecretKey] = useState(null);
  const [currentStep, setCurrentStep] = useState('qr'); // 'qr' or 'verify'
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const onSuccess = (response) => {
    if (response.user) {
      identifyFullStoryUser(response.user);
    }
    if (onComplete) {
      onComplete(response);
    } else {
      handleUserRedirect(response.user, history, {
        isImpersonation: false,
      });
    }
  };

  const onCancel = () => {
    history.push('/member/login');
  };

  const extractSecretFromUri = (uri) => {
    try {
      const url = new URL(uri);
      return url.searchParams.get('secret');
    } catch (e) {
      return null;
    }
  };

  useEffect(() => {
    getProvisioningUri()
      .then((response) => {
        setProvisioningUri(response.provisioning_uri);
        setSecretKey(extractSecretFromUri(response.provisioning_uri));
      })
      .catch(() => {
        setError('Failed to load QR code. Please try again.');
      });
  }, []);

  const handleOtpSubmit = async (otpCode) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await confirmOtp(otpCode);

      if (response.success) {
        onSuccess(response);
      } else {
        setError(response.error || 'Invalid verification code. Please try again.');
      }
    } catch (e) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNextStep = () => {
    setCurrentStep('verify');
    setError(null);
  };

  const handleBackToQR = () => {
    setCurrentStep('qr');
    setError(null);
  };

  const renderQRStep = () => (
    <div className={styles.auth_card}>
      <div className={styles.auth_card_header}>
        <h1 className={styles.auth_heading}>Set up two-factor authentication</h1>
        <p className={styles.auth_subtitle}>
          Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
        </p>
      </div>
      {error && <p className={styles.auth_error}>{error}</p>}
      <div className={styles.qr_container}>
        {provisioningUri ? (
          <QRCode value={provisioningUri} size={200} />
        ) : (
          <div className={styles.qr_loading}>Loading QR code…</div>
        )}
      </div>
      {secretKey && (
        <div className={styles.manual_key_section}>
          <p className={styles.manual_key_label}>Can't scan? Enter this key manually:</p>
          <code className={`${styles.manual_key} fs-mask`}>{secretKey}</code>
        </div>
      )}
      <button type='button' onClick={handleNextStep} className={styles.auth_button} disabled={!provisioningUri}>
        Next
      </button>
      <button type='button' onClick={onCancel} className={styles.auth_button_outline}>
        Cancel
      </button>
    </div>
  );

  const renderVerifyStep = () => (
    <div className={styles.auth_card}>
      <div className={styles.auth_card_header}>
        <h1 className={styles.auth_heading}>Verify your setup</h1>
        <p className={styles.auth_subtitle}>Enter the 6-digit code from your authenticator app to complete setup</p>
      </div>
      <OtpEntry
        standalone={false}
        onSubmit={handleOtpSubmit}
        onSuccess={onSuccess}
        isLoading={isLoading}
        error={error}
      />
      <button
        type='button'
        onClick={handleBackToQR}
        className={`${styles.auth_link} ${styles.auth_link_center}`}
        disabled={isLoading}
      >
        Back to QR code
      </button>
      <button type='button' onClick={onCancel} className={styles.auth_button_outline} disabled={isLoading}>
        Cancel
      </button>
    </div>
  );

  return (
    <div className={styles.auth_page}>
      <div className={styles.auth_page_content}>{currentStep === 'qr' ? renderQRStep() : renderVerifyStep()}</div>
    </div>
  );
};

export default OtpRegistration;
